import TextField from "@material-ui/core/TextField";
import ClassNames from "classnames";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { useNotification } from "../../context/notification";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";
import { utcToLocal } from "../../utils/datetime";
import AlertDialog from "../AlertDialog";
import Button from "../Button";
import DatePickerGroup from "../DatePickerGroup";
import styles from "./EditCoachingDate.module.scss";

function EditCoachingDate({
  setEditModalOpen,
  isOpen,
  isDisableMTD = false,
  sessionDetails,
  setIsDateEdited,
  redirectToSessionDetails = false,
  coachingDateHistoryList,
  subjectDetails,
  setSubjectDetails,
  hideProjectTargetKpi,
}) {
  const history = useHistory();
  const [coachingDate, setCoachingDate] = useState(null);
  const [reviewDate, setReviewDate] = useState(null);
  const [postponeReason, setPostponeReason] = useState("");
  const [isPostponeReasonEmpty, setIsPostponeReasonEmpty] = useState(false);
  const [specialCharacterError, setSpecialCharacterError] = useState(false);
  const [maxDateToPostpone, setMaxDateToPostpone] = useState(new Date());
  const [isPDFlow, setIsPDFlow] = useState(sessionDetails?.framework === 2);

  const { t } = useTranslation();
  const { addNotification } = useNotification();

  const handleDateChange = (field, date) => {
    if (field === "coaching") {
      setCoachingDate(date);
      if (
        !isPDFlow &&
        utcToLocal(date).add(2, "day").format("YYYY-MM-DD") >=
        utcToLocal(reviewDate).format("YYYY-MM-DD")
      ) {
        setReviewDate(dayjs(date).add(2, "days"));
      }
    } else if (field === "review") {
      setReviewDate(date);
    }
  };
  useEffect(() => {
    if (sessionDetails) {
      setPostponeReason("");
      setIsPDFlow(sessionDetails.framework === 2);

      let defaultCoachingDate = sessionDetails.session_datetime;
      if (coachingDateHistoryList.length > 0) {
        defaultCoachingDate = coachingDateHistoryList[0].previous_coaching_date;
      }
      setMaxDateToPostpone(
        dayjs(defaultCoachingDate)
          .add(1, "month")
          .endOf("month")
          .format("D MMM YYYY h:mm A")
      );
    }
  }, [sessionDetails, coachingDateHistoryList]);

  const handleValueChange = (index, newValue, field) => {
    const updatedSubjectDetails = [...subjectDetails];
    updatedSubjectDetails[index][field] = newValue;
    setSubjectDetails(updatedSubjectDetails); // Use the function to update subjectDetails
  };

  const handleUpdate = () => {
    const previousCoachingDate = dayjs(sessionDetails.session_datetime)
      .add(dayjs().utcOffset(), "minutes")
      .format("D MMM YYYY h:mm A");
    const previousReviewDate = !isPDFlow
      ? dayjs(sessionDetails.review_datetime)
        .add(dayjs().utcOffset(), "minutes")
        .format("D MMM YYYY h:mm A")
      : null;

    if (
      (!reviewDate && !isPDFlow) ||
      !coachingDate ||
      (previousCoachingDate === coachingDate &&
        previousReviewDate === reviewDate)
    ) {
      addNotification(
        `You have not updated any date, please edit coaching or review date or click cancel`,
        "error"
      );
      return;
    }

    const trimmed = postponeReason.trim();

    if (!trimmed) {
      setIsPostponeReasonEmpty(true);
      return;
    }

    const updatedSubjectDetails = subjectDetails.map(item => {
      return {
        subject_id: item.subject_id,
        subject: item.subject,
        sub_subject: item.sub_subject,
        project_target_kpi: item.project_target_kpi,
        mtd_metric: item.mtd_metric,
      };
    });
    const formObj = {
      session_id: sessionDetails.session_id,
      session_type_id: sessionDetails.session_type_id,
      created_by: parseInt(getLocalValue("uid")),
      reason: postponeReason,
      subject: updatedSubjectDetails,
      framework: sessionDetails.framework,
      ...(previousCoachingDate !== coachingDate && {
        new_coaching_date: dayjs(coachingDate)
          .utc()
          .format("YYYY-MM-DD HH:mm:ss"),
        previous_coaching_date: sessionDetails.session_datetime,
      }),
      ...(previousReviewDate !== reviewDate &&
        !isPDFlow && {
        new_review_date: dayjs(reviewDate)
          .utc()
          .format("YYYY-MM-DD HH:mm:ss"),
        previous_review_date: sessionDetails.review_datetime,
      }),
    };

    apiRequest
      .patch(`/api/postponeCoachingDate`, formObj)
      .then(resp => {
        setReviewDate(null);
        setCoachingDate(null);
        setEditModalOpen(false);

        if (resp.status === 200) {
          setIsDateEdited(true);
          addNotification("Sessions date was updated", "success");

          if (redirectToSessionDetails) {
            history.push(`/sessions/details/${sessionDetails.session_id}`);
          }
        }

        if (resp.status === 400) {
          throw new Error(`${resp.data.error}: ${resp.data.property}`);
        }
      })
      .catch(error => {
        addNotification(`${error}`, "error");
      });
  };

  return (
    <div>
      <AlertDialog
        maxWidth="xs"
        open={isOpen}
        onClose={() => {
          setEditModalOpen(false);
        }}
        title={t("editSessionCoachingDates.title")}
        body={
          <>
            <div className={ClassNames(styles.row, styles.mediumGutters)}>
              <div
                className={styles.rowLeft}
                style={{
                  paddingTop: "12px",
                }}
              >
                {t("createSession.coachingDate")}
              </div>
              <div className={styles.rowRight}>
                <DatePickerGroup
                  disablePast
                  selectedDate={coachingDate}
                  maxDate={maxDateToPostpone}
                  handleDateChange={date => {
                    handleDateChange("coaching", date);
                  }}
                />
              </div>
            </div>
            {sessionDetails && !isPDFlow && (
              <div className={ClassNames(styles.row, styles.mediumGutters)}>
                <div
                  className={styles.rowLeft}
                  style={{
                    paddingTop: "12px",
                  }}
                >
                  {t("createSession.reviewDate")}
                </div>
                <div className={styles.rowRight}>
                  <DatePickerGroup
                    disablePast
                    selectedDate={reviewDate}
                    minDate={dayjs(coachingDate).add(2, "days")}
                    maxDate={dayjs(coachingDate).add(1, "month").endOf("month")}
                    handleDateChange={date => {
                      handleDateChange("review", date);
                    }}
                  />
                </div>
              </div>
            )}

            <div className={ClassNames(styles.row, styles.noPaddingBottom)}>
              <div
                className={ClassNames(styles.rowLeft, styles.subjectListLabel)}
              >
                {t("sessionDetails.subjects")}
              </div>
              <div
                className={ClassNames(
                  styles.rowRight,
                  styles.subjectListWrapper
                )}
              >
                <div className={styles.container}>
                  <div className={styles.subjectList}>
                    {subjectDetails.map((item, index) => {
                      return (
                        <div key={index} className={styles.listItem}>
                          <div className={styles.index}>
                            <div>{index + 1}</div>
                          </div>
                          <div className={styles.listItemDetails}>
                            <div
                              className={styles.subjectName}
                              title={item.subject}
                            >
                              {item.subject}
                              {item.sub_subject ? " — " + item.sub_subject : ""}
                            </div>
                            {!hideProjectTargetKpi && (
                              <div className={styles.subjectMetrics}>
                                <label htmlFor={`projectTargetKPI-${index}`}>
                                  {t("growForm.projectTargetKpi")}{" "}
                                </label>
                                <TextField
                                  variant="outlined"
                                  size="medium"
                                  type="number"
                                  id={`projectTargetKPI-${index}`}
                                  className={styles.subjectMetricsValue}
                                  title={item.project_target_kpi}
                                  defaultValue={item.project_target_kpi}
                                  onChange={e =>
                                    handleValueChange(
                                      index,
                                      e.target.value,
                                      "project_target_kpi"
                                    )
                                  }
                                />
                              </div>
                            )}
                            {!hideProjectTargetKpi && (
                              <div className={styles.subjectMetrics}>
                                <label htmlFor={`mtdMetric-${index}`}>
                                  {t("growForm.mtd")}
                                  {"  "}
                                </label>
                                <TextField
                                  variant="outlined"
                                  size="medium"
                                  type="number"
                                  id={`mtd_metric-${index}`}
                                  className={styles.subjectMetricsValue}
                                  title={item.mtd_metric}
                                  defaultValue={item.mtd_metric}
                                  disabled={isDisableMTD}
                                  onChange={e =>
                                    handleValueChange(
                                      index,
                                      e.target.value,
                                      "mtd_metric"
                                    )
                                  }
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <TextField
              className={styles.textarea}
              multiline
              rows={4}
              inputProps={{ style: { resize: "both" } }}
              fullWidth
              placeholder={t("updateCheckpoint.pleaseStateReason")}
              variant="outlined"
              onChange={e => {
                const val = e.target.value;
                setPostponeReason(e.target.value);
                setIsPostponeReasonEmpty(val ? false : true);
              }}
              error={isPostponeReasonEmpty || specialCharacterError}
              helperText={
                isPostponeReasonEmpty
                  ? "Please add the reason"
                  : specialCharacterError &&
                  "Special characters are not allowed"
              }
            />
          </>
        }
        footer={
          <>
            <Button
              size="small"
              onClick={() => {
                setCoachingDate(null);
                setReviewDate(null);
                setEditModalOpen(false);
              }}
            >
              {t("updateCheckpoint.cancel")}
            </Button>
            <Button
              color="primary"
              onClick={handleUpdate}
              variant="contained"
              size="small"
            >
              {t("editSessionCoachingDates.update")}
            </Button>
          </>
        }
      />
    </div>
  );
}

export default EditCoachingDate;
